import { useLocale, useTranslations } from "use-intl";
import { useAppDateTime } from "@/hooks/use-app-date-time";
import { hasLargestCatalogEvidence } from "@/lib/catalog-claim";
const content = {
  en: <>
      <h1>Physical Music Catalog Data Providers and Methodology</h1>
      <p>
        Garimpa.Club builds one linked catalog from online music-data providers, carefully curated
        reference datasets and reviewed catalog curation so collectors can find the exact physical
        edition they own or want. The exact release and work counts shown above are produced from
        the same published catalog used by the homepage and product, not from raw provider totals,
        import inputs or estimated database statistics.
      </p>
      <h2>What the Catalog Count Includes</h2>
      <p>
        The physical-release count includes distinct published editions manufactured on formats such
        as vinyl, CD, cassette, MiniDisc, shellac and music video. Digital-only downloads and
        streaming releases are filtered out before records enter Garimpa&apos;s provider-neutral
        catalog, linkage and publication stages.
      </p>
      <p>
        Each published release is counted once. Provider records are normalized into shared fields
        and linked using stable source identifiers and release evidence such as artists, works,
        labels, catalog numbers, barcodes, countries, dates, formats and track relationships.
        Records that describe the same physical edition are merged into one Garimpa release while
        their source provenance remains attached. Different pressings, formats, countries, label
        editions or other materially distinct physical releases remain separate.
      </p>
      <p>
        The catalog count is recomputed exactly from the published physical-only release model
        during each catalog publication. The “Catalog counted on” date identifies the build used for
        the displayed count. We do not add marketplace listings, retailer offers, raw source rows or
        approximate PostgreSQL table statistics to that number.
      </p>
      <h2>Source Coverage</h2>
      <h3>Discogs</h3>
      <p>
        <a href="https://www.discogs.com">Discogs</a> contributes detailed community-maintained
        metadata about releases, artists, labels, formats, tracks, catalog numbers, barcodes and
        country-specific editions. It is an important source for distinguishing physical pressings
        and packaging variations.
      </p>
      <h3>MusicBrainz</h3>
      <p>
        <a href="https://musicbrainz.org">MusicBrainz</a> contributes open identifiers and
        structured relationships across artists, releases, release groups, labels, recordings,
        places and external resources. These relationships help connect equivalent entities across
        sources without erasing provenance.
      </p>
      <h3>Wikimedia, Wikipedia and Wikidata</h3>
      <p>
        <a href="https://wikimediafoundation.org">Wikimedia Foundation</a> projects, including{" "}
        <a href="https://www.wikipedia.org">Wikipedia</a> and{" "}
        <a href="https://www.wikidata.org">Wikidata</a>, contribute public knowledge, identifiers,
        reference links and historical context for artists, companies, works and releases.
      </p>
      <h3>Fanart.tv</h3>
      <p>
        <a href="https://fanart.tv">Fanart.tv</a> contributes artist and album artwork that makes
        catalog browsing more recognizable while remaining separate from release identity and count
        decisions.
      </p>
      <h3>Connected services</h3>
      <p>
        Services such as <a href="https://www.last.fm">Last.fm</a> and Spotify can connect a
        user&apos;s listening activity and music preferences to Garimpa features. They do not
        increase the published physical-release count unless a physical release is independently
        represented and linked in the catalog.
      </p>
      <h2>Curated Sources and AI-Assisted Curation</h2>
      <p>
        Garimpa also maintains carefully curated seed and reference datasets for catalog imports.
        These can represent additional documented sources beyond the online providers named above,
        including specialist discographies and release research prepared for catalog coverage. Every
        imported record remains provenance-bearing evidence: it is normalized into the same
        provider-neutral model, linked using shared catalog evidence, and must pass the same
        physical-only and publication-quality checks. Once data enters that neutral model, linkage
        and publication rules do not depend on which source supplied it.
      </p>
      <p>
        AI-assisted curation helps reviewers identify potential coverage gaps, duplicates,
        conflicting facts and candidate links between artists, works and releases. These results are
        suggestions for investigation, not automatic catalog truth. They are reviewed against source
        evidence and catalog constraints before they can affect published data, and provenance is
        retained so accepted decisions remain traceable and correctable.
      </p>
      <p>
        Provider coverage changes as sources improve, new releases are documented and linkage is
        refined. A source&apos;s presence does not mean every one of its records is published or
        that every physical release in the world has already been documented.
      </p>
      <h2>Catalog Sources and Shopping Providers Are Different</h2>
      <p>
        Catalog-data providers supply evidence used to describe and link artists, works, companies
        and physical releases. Shopping providers and collector listings supply current
        availability, price, shipping and purchase links. Shopping offers do not create catalog
        releases, and offer counts are never included in the physical-release count.
      </p>
      <p>
        Retailer or seller identities are displayed only with offer-backed results. Garimpa does not
        sell those items directly and does not control a third party&apos;s inventory, price,
        fulfillment or terms. Some outbound shopping links are affiliate links, which may earn
        Garimpa a commission without changing the buyer&apos;s price. Listing a data source,
        retailer or seller does not imply sponsorship, partnership or endorsement.
      </p>
      <h2>How We Verify the “World&apos;s Largest” Description</h2>
      <p>
        The comparison was verified on <strong>July 13, 2026</strong>. We compare Garimpa&apos;s
        exact, dated count of distinct published physical releases with the largest publicly
        documented, release-level catalog counts available from comparable music-catalog services.
        We compare release entities with release entities: raw submissions, marketplace listings,
        sale offers, collection items, recordings, tracks and combined digital-plus-physical totals
        are not treated as equivalent physical-release counts.
      </p>
      <p>
        For each comparator we retain its public source, the metric&apos;s definition and the
        verification date. If a service does not publish a count that can be isolated to
        release-level physical editions, we record that limitation instead of presenting an
        unsupported numerical comparison. Garimpa displays the superlative and the numeric proof
        together only while its exact eligible count exceeds the highest verified comparable public
        count. If the evidence or current Garimpa count is unavailable, the homepage falls back to a
        non-superlative description.
      </p>
      <p>
        This method is designed to make the claim testable rather than permanent: counts and public
        methodologies can change, so the comparison must be reverified and the dated catalog proof
        must remain available.
      </p>
      <h2>Provenance and Corrections</h2>
      <p>
        Garimpa keeps source identifiers and links wherever available so catalog facts can be traced
        back to their contributing providers. Normalization and linkage create a unified browsing
        model; they do not transfer ownership of source data to Garimpa or erase the original
        communities&apos; work.
      </p>
      <p>
        This catalog would not exist without the people who document editions, identify credits,
        maintain open identifiers and preserve music history. If you find a release that is missing,
        duplicated or linked incorrectly, please <a href="/en/contact">contact us</a> with the
        release and source references so we can investigate the published evidence.
      </p>
    </>,
  pt: <>
      <h1>Fontes de dados e metodologia do catálogo</h1>
      <p>
        O Garimpa.Club constrói um catálogo vinculado de{" "}
        <strong>lançamentos musicais físicos</strong>. Reunimos registros de várias fontes,
        normalizamos estruturas diferentes e conectamos entradas que descrevem a mesma obra ou a
        mesma edição. Assim, quem coleciona pode pesquisar artistas, obras, lançamentos, selos,
        formatos, códigos de barras, números de catálogo, imagens e links de origem em um só lugar.
      </p>
      <p>
        A contagem exata de lançamentos físicos, o total de obras vinculadas e a data da contagem
        aparecem acima e na página inicial. Esses números vêm dos modelos publicados do catálogo, e
        não da soma de registros brutos enviados pelos provedores nem de estimativas do banco de
        dados.
      </p>
      <h2>O que entra na contagem</h2>
      <ul>
        <li>
          Contamos apenas lançamentos publicados no catálogo físico do Garimpa: vinil, CD, fita
          cassete, vídeo e outros suportes tangíveis.
        </li>
        <li>
          Edições exclusivamente digitais são filtradas nas etapas específicas de cada provedor
          antes de chegarem ao modelo comum.
        </li>
        <li>
          Registros equivalentes de fontes diferentes são vinculados por identidades estruturadas e
          evidências de edição; não são contados como novos lançamentos apenas por terem vindo de
          outro provedor.
        </li>
        <li>
          A contagem representa edições físicas distintas, enquanto “obras vinculadas” representa os
          agrupamentos musicais aos quais essas edições pertencem.
        </li>
        <li>
          Mantemos a proveniência de cada registro e os links de origem disponíveis para revisão.
        </li>
      </ul>
      <h2>Como verificamos a afirmação de maior catálogo</h2>
      <p>
        A comparação pública foi verificada em <strong>13 de julho de 2026</strong>. Comparamos o
        número exato de edições físicas distintas publicado pelo Garimpa com as contagens públicas e
        diretamente comparáveis divulgadas por serviços globais de catálogo musical. Excluímos
        números que misturam faixas, gravações, obras, itens digitais, anúncios de marketplace ou
        registros brutos duplicados, porque essas unidades não são comparáveis a edições físicas
        distintas.
      </p>
      <p>
        Uma comparação só é considerada válida quando o serviço concorrente publica uma definição
        clara da unidade contada, uma data ou atualização verificável e cobertura global. Quando não
        há contagem pública equivalente, registramos a ausência de evidência comparável em vez de
        estimar. O Garimpa só exibe a afirmação “maior catálogo do mundo” junto da sua contagem
        exata e datada quando essa evidência está disponível; caso contrário, usa uma descrição sem
        o superlativo.
      </p>
      <h2>Fontes do catálogo</h2>
      <p>
        As fontes abaixo fornecem dados de catálogo, contexto ou mídia. Elas não são, por isso,
        lojas nem fornecedoras das ofertas exibidas pelo Garimpa.
      </p>
      <h3>Discogs</h3>
      <p>
        O <a href="https://www.discogs.com">Discogs</a> fornece dados detalhados de lançamentos,
        selos, artistas, formatos, faixas, códigos de barras e números de catálogo contribuídos por
        uma grande comunidade de colecionadores. Ele é especialmente importante para edições
        físicas, variações de selos e lançamentos específicos por país.
      </p>
      <p>
        Obrigado ao Discogs e a cada pessoa colaboradora que dedicou tempo a documentar os detalhes
        que tornam a coleção de discos pesquisável e útil.
      </p>
      <h3>MusicBrainz</h3>
      <p>
        O <a href="https://musicbrainz.org">MusicBrainz</a> fornece identificadores musicais abertos
        e relações estruturadas entre artistas, lançamentos, grupos de lançamento, selos, gravações,
        áreas e links externos. Ele ajuda o Garimpa.Club a conectar entradas equivalentes entre
        fontes e manter a proveniência visível.
      </p>
      <p>
        Obrigado ao MusicBrainz, à MetaBrainz e à comunidade de editores por construírem uma base
        aberta e durável de dados musicais.
      </p>
      <h3>Wikimedia, Wikipedia e Wikidata</h3>
      <p>
        Os projetos da <a href="https://wikimediafoundation.org">Wikimedia Foundation</a>, incluindo
        a <a href="https://www.wikipedia.org">Wikipedia</a> e o{" "}
        <a href="https://www.wikidata.org">Wikidata</a>, ajudam a fornecer conhecimento público,
        contexto enciclopédico, identificadores e links de referência sobre artistas, selos,
        gravações, lançamentos e história da música.
      </p>
      <p>
        Obrigado à Wikimedia e às comunidades globais de editores por manterem conhecimento cultural
        aberto que torna os dados de catálogo mais fáceis de entender e verificar.
      </p>
      <h3>Fanart.tv</h3>
      <p>
        O <a href="https://fanart.tv">Fanart.tv</a> fornece imagens de artistas e álbuns que ajudam
        a tornar a navegação pelas coleções mais visual e reconhecível.
      </p>
      <p>
        Obrigado ao Fanart.tv e a seus colaboradores por tornarem a descoberta musical mais rica sem
        perder de vista os dados de catálogo por baixo.
      </p>
      <h3>Last.fm</h3>
      <p>
        O <a href="https://www.last.fm">Last.fm</a> ajuda a conectar atividade de escuta, scrobbles
        e gosto musical de volta à experiência do catálogo para usuários que escolhem conectá-lo.
      </p>
      <p>
        Obrigado ao Last.fm por manter o histórico de escuta portátil e útil entre ferramentas
        musicais.
      </p>
      <h2>Fontes curadas e curadoria assistida por IA</h2>
      <p>
        Além dos provedores on-line mencionados acima, o Garimpa usa bases de referência
        cuidadosamente curadas nas importações do catálogo. Essas bases podem representar fontes
        adicionais que não aparecem individualmente nesta página. Cada contribuição continua sendo
        tratada como evidência com proveniência: sua origem é preservada, os dados são normalizados
        e vinculados ao modelo comum, e os registros passam pelos mesmos filtros de lançamentos
        exclusivamente físicos e pelas verificações de qualidade aplicadas às demais fontes.
      </p>
      <p>
        A IA auxilia a curadoria ao apontar possíveis lacunas, duplicações, conflitos e vínculos
        entre registros. Essas indicações servem como apoio à revisão: são confrontadas com
        evidências e regras do catálogo e não se tornam automaticamente verdade publicada. A decisão
        final permanece sustentada por proveniência verificável, dados estruturados e controles de
        qualidade.
      </p>
      <h2>Como vinculamos e publicamos os dados</h2>
      <p>
        Os dados de cada fonte passam primeiro por filtros e transformações próprios do provedor.
        Depois, entram em um modelo comum independente de fonte, no qual identificadores, relações
        estruturadas e evidências da edição sustentam a vinculação. Os modelos finais publicados
        alimentam a busca, as páginas de detalhe, as ferramentas de coleção, as listas de desejos, a
        descoberta e as contagens exibidas no site.
      </p>
      <p>
        Mantemos links de origem quando disponíveis e não tratamos a origem do dado como endosso ao
        Garimpa. Erros podem existir nos conjuntos de dados de origem ou no processo de vinculação;
        a proveniência visível permite conferir de onde veio cada informação.
      </p>
      <h2>Lojas, marketplaces e links de ofertas</h2>
      <p>
        As ofertas de compra são uma camada separada do catálogo. Elas podem vir de lojas
        conectadas, marketplaces e anúncios de colecionadores, e a identidade do vendedor aparece
        somente quando existe uma oferta ativa vinculada a um lançamento compatível.
      </p>
      <p>
        O Garimpa não vende esses itens diretamente e nenhuma fonte de catálogo endossa as lojas ou
        ofertas mostradas. Alguns links para lojas são links de afiliado ou patrocinados: o Garimpa
        pode receber uma comissão quando uma compra é concluída, sem custo adicional para quem
        compra. Disponibilidade, preço, frete, condição e conclusão da compra são responsabilidade
        do vendedor.
      </p>
      <h2>Agradecimentos</h2>
      <p>
        Este catálogo não existiria sem as comunidades e serviços que tornam metadados musicais
        disponíveis. Agradecemos a todas as pessoas que documentam edições, identificam créditos,
        mantêm identificadores abertos, melhoram históricos de artistas e selos e preservam o
        contexto visual da música gravada.
      </p>
    </>,
  es: <>
      <h1>Proveedores de datos y metodología del catálogo</h1>
      <p>
        Garimpa.Club construye un único catálogo vinculado a partir de varias fuentes musicales.
        Nuestro alcance publicado incluye exclusivamente{" "}
        <strong>ediciones musicales físicas</strong>: vinilos, CD, casetes, vídeos y otros soportes
        tangibles. Las ediciones exclusivamente digitales se descartan antes de la vinculación y no
        forman parte del recuento.
      </p>
      <p>
        El recuento exacto y fechado que aparece arriba y en la página de inicio procede de los
        modelos publicados del catálogo, no de estimaciones de la base de datos ni de la suma de
        registros brutos de los proveedores. Si ese dato verificado no está disponible, Garimpa no
        muestra ni la cifra ni la afirmación comparativa.
      </p>
      <h2>Cómo vinculamos y deduplicamos los datos</h2>
      <p>
        Los proveedores describen muchas veces la misma obra o edición con identificadores y niveles
        de detalle diferentes. Garimpa normaliza sus campos en un modelo común y vincula identidades
        compatibles conservando las relaciones entre obra, edición, artista, sello y empresa. El
        recuento de ediciones físicas se calcula sobre las ediciones publicadas y deduplicadas; no
        suma copias repetidas de la misma entidad aportadas por varias fuentes.
      </p>
      <p>
        La vinculación no borra la procedencia. Siempre que existe, conservamos el identificador y
        el enlace de origen para que los datos puedan rastrearse y contrastarse. Las cifras de
        obras, artistas y empresas también se calculan sobre las entidades vinculadas del catálogo
        publicado.
      </p>
      <h2>Fuentes seleccionadas y curación asistida por IA</h2>
      <p>
        Además de los proveedores en línea descritos a continuación, Garimpa incorpora datos semilla
        y conjuntos de referencia cuidadosamente seleccionados para importar catálogos. Estos
        materiales pueden representar fuentes adicionales que no aparecen en la lista de servicios
        conectados. Cada aportación se conserva como evidencia con su propia procedencia, se
        normaliza y vincula con el mismo modelo común y debe superar los controles de alcance físico
        y calidad antes de llegar al catálogo publicado.
      </p>
      <p>
        La curación asistida por IA ayuda a identificar posibles vacíos, duplicados, conflictos
        entre fuentes y vínculos candidatos. Sus resultados son sugerencias para la revisión del
        catálogo: se evalúan junto con la evidencia disponible y no se convierten automáticamente en
        la verdad del catálogo. La publicación sigue dependiendo de controles explícitos y de datos
        cuya procedencia pueda rastrearse.
      </p>
      <h2>Fuentes de datos del catálogo</h2>
      <h3>Discogs</h3>
      <p>
        <a href="https://www.discogs.com">Discogs</a> aporta datos detallados sobre ediciones,
        formatos, artistas, sellos, pistas, países, números de catálogo y códigos de barras
        documentados por su comunidad de coleccionistas.
      </p>
      <h3>MusicBrainz</h3>
      <p>
        <a href="https://musicbrainz.org">MusicBrainz</a> aporta identificadores abiertos y
        relaciones estructuradas entre artistas, obras, grupos de ediciones, ediciones, grabaciones,
        sellos, zonas y enlaces externos.
      </p>
      <h3>Wikimedia, Wikipedia y Wikidata</h3>
      <p>
        Los proyectos de la <a href="https://wikimediafoundation.org">Fundación Wikimedia</a>, entre
        ellos <a href="https://www.wikipedia.org">Wikipedia</a> y{" "}
        <a href="https://www.wikidata.org">Wikidata</a>, aportan contexto público, identificadores,
        referencias y conocimiento enciclopédico.
      </p>
      <h3>Fanart.tv</h3>
      <p>
        <a href="https://fanart.tv">Fanart.tv</a> aporta imágenes de artistas y álbumes que
        enriquecen visualmente la navegación sin sustituir los datos estructurados del catálogo.
      </p>
      <h3>Servicios conectados</h3>
      <p>
        Servicios como <a href="https://www.last.fm">Last.fm</a> y Spotify pueden conectar la
        actividad de escucha de quien decida autorizarlos con su experiencia en Garimpa. Estas
        integraciones no se contabilizan como fuentes de ediciones físicas.
      </p>
      <p>
        Agradecemos a las comunidades, editores y servicios que hacen posible documentar y conservar
        la historia de las ediciones musicales.
      </p>
      <h2>Proveedores de datos y proveedores comerciales</h2>
      <p>
        Las fuentes anteriores alimentan el <strong>catálogo</strong>. Los comercios y mercados que
        muestran artículos a la venta son <strong>proveedores comerciales</strong> independientes.
        Garimpa solo muestra la identidad de un comercio cuando existe una oferta activa vinculada a
        una edición del catálogo; sus datos comerciales no se utilizan para inflar el recuento del
        catálogo.
      </p>
      <p>
        Garimpa no vende directamente los artículos de esos comercios. Algunos enlaces de compra son
        enlaces de afiliación y pueden generar una comisión para Garimpa sin coste adicional para la
        persona compradora. La inclusión de una fuente, servicio o comercio no implica que esa
        organización respalde a Garimpa ni que Garimpa la respalde.
      </p>
      <h2>Método de comparación</h2>
      <p>
        Para evaluar la afirmación sobre el tamaño del catálogo, comparamos nuestro recuento exacto
        de ediciones físicas publicadas y deduplicadas con los recuentos públicos a nivel de{" "}
        <strong>edición</strong> que ofrecen otros catálogos musicales. No comparamos nuestro total
        con cifras de obras, álbumes conceptuales, grupos de ediciones, anuncios comerciales ni
        registros brutos, porque representan unidades diferentes.
      </p>
      <p>
        La comparación se verificó el <strong>13 de julio de 2026</strong>. La cifra del catálogo de
        Garimpa se vuelve a calcular en cada publicación y se muestra con la fecha en que fue
        contabilizada; la fecha de verificación comparativa se mantiene separada para dejar claro
        qué se midió y cuándo.
      </p>
    </>
} satisfies Record<ViewerLocale, ReactNode>;
import { Route } from "/app/apps/web/src/routes/_pages/$lang/_about/about/providers.tsx";
function DataProvidersPage() {
  const locale = useLocale() as ViewerLocale;
  const {
    stats
  } = Route.useLoaderData();
  const t = useTranslations("home");
  const number = new Intl.NumberFormat(locale);
  const {
    longDate
  } = useAppDateTime();
  return <div className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
      <article className="prose prose-invert">
        {stats ? <aside className="not-prose mb-10 rounded-xl border border-primary/30 bg-primary/10 p-6 text-center sm:p-8">
            <p>
              <span className="block text-5xl font-black tracking-tight text-primary sm:text-6xl">
                {number.format(stats.physicalReleaseCount + stats.workCount + stats.artistCount + stats.companyCount)}
              </span>
              <span className="mt-2 block text-base font-semibold text-foreground sm:text-lg">
                {t("providerTotalItems")}
              </span>
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground">
              {t("providerCountProof", {
            releases: number.format(stats.physicalReleaseCount),
            works: number.format(stats.workCount),
            artists: number.format(stats.artistCount),
            companies: number.format(stats.companyCount),
            date: longDate(stats.catalogBuiltAt)
          })}
            </p>
            {hasLargestCatalogEvidence(stats) ? <p className="mt-3 text-sm font-semibold text-primary">
                {t("providerComparisonProof")}
              </p> : null}
          </aside> : null}
        {content[locale]}
      </article>
    </div>;
}
export { DataProvidersPage as component };